/**
 * Two LINKED Chart.js panels for one TEK_RSI_MOMENTUM trade: NIFTY 5-minute
 * candles (+VWAP, EMA(20), entry/stop/target, BUY/SELL and EXIT markers,
 * profit/loss zones) and the RSI momentum panel (RSI(9), EMA(3)/WMA(21) of
 * RSI, 50 line, raw crossovers, actual entry signal, blocked signals, exit).
 *
 * Uses the chart.js + react-chartjs-2 already in the app (candles drawn as
 * floating bars, same technique as Rsi2CandleChart). There is no zoom
 * plugin installed, so zoom/pan is a shared visible window (buttons +
 * range slider) applied to both x-axes -- keeping the panels in sync.
 */
import { useMemo, useRef } from "react";
import { Box, Button, ButtonGroup, Slider, Stack, Typography } from "@mui/material";
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
  type Chart as ChartInstance,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import type { RsiTradeReview } from "@/types";
import { buildLabelPlugin, buildPriceChart, buildRsiChart, focusWindow, pan, priceRange, zoom, type Toggles, type Window } from "./tradeReviewChartData";

ChartJS.register(BarController, BarElement, LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler, ChartTooltip, Legend);

const PRICE_LABELS = "tradeReviewPriceLabels";
const RSI_LABELS = "tradeReviewRsiLabels";
const priceLabelPlugin = buildLabelPlugin(PRICE_LABELS);
const rsiLabelPlugin = buildLabelPlugin(RSI_LABELS);

/* eslint-disable @typescript-eslint/no-explicit-any -- Chart.js mixed dataset/options typing (see tradeReviewChartData). */
export default function TradeReviewCharts({
  review,
  toggles,
  window: win,
  onWindow,
}: {
  review: RsiTradeReview;
  toggles: Toggles;
  window: Window;
  onWindow: (w: Window) => void;
}) {
  const priceRef = useRef<ChartInstance<any> | null>(null);
  const rsiRef = useRef<ChartInstance<any> | null>(null);
  const n = review.candles.length;
  const price = useMemo(() => buildPriceChart(review, toggles), [review, toggles]);
  const rsi = useMemo(() => buildRsiChart(review, toggles), [review, toggles]);
  const yRange = useMemo(() => priceRange(review, win, toggles), [review, win, toggles]);

  const xAxis = (showTicks: boolean): any => ({
    type: "category", min: win.start, max: win.end, offset: true, grouped: false, stacked: false,
    ticks: { display: showTicks, maxRotation: 0, autoSkip: true, maxTicksLimit: 14 },
    grid: { color: "rgba(0,0,0,0.05)" },
  });
  const common = (labelsId: string, labels: unknown, yOpts: any): any => ({
    responsive: true, maintainAspectRatio: false, animation: false,
    interaction: { mode: "index", intersect: false },
    scales: { x: xAxis(true), y: yOpts },
    plugins: {
      legend: { position: "top", labels: { boxWidth: 12, font: { size: 11 }, filter: (i: any) => i.text !== "Wick" } },
      tooltip: {
        callbacks: {
          title: (items: any[]) => (items[0] ? `${review.summary.trade_date} ${items[0].label} IST` : ""),
          label: (item: any) => {
            const v = item.raw;
            if (Array.isArray(v)) {
              const c = review.candles[item.dataIndex];
              return item.dataset.label === "Wick" ? "" : `O ${c.open} H ${c.high} L ${c.low} C ${c.close}`;
            }
            return v === null || v === undefined ? "" : `${item.dataset.label}: ${Number(v).toFixed(2)}`;
          },
        },
      },
      [labelsId]: { labels },
    },
  });

  const exportPng = () => {
    const a = priceRef.current?.canvas;
    const b = rsiRef.current?.canvas;
    if (!a) return;
    const doc = globalThis.document;
    const out = doc.createElement("canvas");
    out.width = a.width;
    out.height = a.height + (b && toggles.rsi ? b.height : 0);
    const ctx = out.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(a, 0, 0);
    if (b && toggles.rsi) ctx.drawImage(b, 0, a.height);
    const link = doc.createElement("a");
    link.href = out.toDataURL("image/png");
    link.download = `rsi-momentum-trade-${review.summary.trade_date}-${review.summary.id.slice(0, 8)}.png`;
    link.click();
  };

  if (n === 0) {
    return <Typography color="text.secondary">No stored candles for this session — the chart cannot be drawn (see data warnings).</Typography>;
  }
  return (
    <Box data-testid="trade-review-charts">
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <ButtonGroup size="small" variant="outlined">
          <Button aria-label="Zoom in" onClick={() => onWindow(zoom(win, n, 0.7))}>Zoom +</Button>
          <Button aria-label="Zoom out" onClick={() => onWindow(zoom(win, n, 1.4))}>Zoom −</Button>
          <Button aria-label="Pan left" onClick={() => onWindow(pan(win, n, -6))}>◀</Button>
          <Button aria-label="Pan right" onClick={() => onWindow(pan(win, n, 6))}>▶</Button>
          <Button onClick={() => onWindow(focusWindow(review))}>Focus trade</Button>
          <Button onClick={() => onWindow({ start: 0, end: n - 1 })}>Full session</Button>
        </ButtonGroup>
        <Box sx={{ flex: 1, minWidth: 200, px: 2 }}>
          <Slider
            size="small" value={[win.start, win.end]} min={0} max={n - 1} step={1} disableSwap
            valueLabelDisplay="auto" valueLabelFormat={(i) => review.candles[i]?.time ?? ""}
            onChange={(_, v) => { const [a, b] = v as number[]; onWindow({ start: a, end: Math.max(b, a + 1) }); }}
            getAriaLabel={() => "Visible time range"}
          />
        </Box>
        <Button size="small" variant="outlined" onClick={exportPng}>Export PNG</Button>
      </Stack>
      <Box sx={{ height: 380 }} data-testid="price-chart">
        <Chart ref={priceRef} type="bar" data={price.data} plugins={[priceLabelPlugin]}
          options={common(PRICE_LABELS, price.labels, { position: "right", beginAtZero: false, min: yRange?.min, max: yRange?.max, ticks: { callback: (v: any) => Number(v).toLocaleString("en-IN") } })} />
      </Box>
      {toggles.rsi && (
        <Box sx={{ height: 220, mt: 1 }} data-testid="rsi-chart">
          <Chart ref={rsiRef} type="line" data={rsi.data} plugins={[rsiLabelPlugin]}
            options={common(RSI_LABELS, rsi.labels, { position: "right", min: 0, max: 100, ticks: { stepSize: 25 } })} />
        </Box>
      )}
      <Typography variant="caption" color="text.secondary">
        All times IST · completed 5-minute candles only · each indicator value uses that bar and earlier bars only · empty slots are missing candles (never filled in)
      </Typography>
    </Box>
  );
}
/* eslint-enable @typescript-eslint/no-explicit-any */
